import { HttpErrorResponse } from '@angular/common/http';
import {
  Injectable,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isString, flatMap } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { isCommandResult } from 'webcommon/shared';

import { ApErrorHandler } from './ApErrorHandler';

const genericErrorMessage = 'An error has occurred and the process cannot be completed.';

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  constructor(
    private readonly apErrorHandler: ApErrorHandler,
    private readonly translateService: TranslateService,
  ) {
  }

  getErrors(error: unknown, skipReporting?: boolean): string[] {
    if (!error) {
      return [genericErrorMessage];
    }

    if (isString(error)) {
      return [error];
    }

    if (error instanceof Error) {
      if (!skipReporting) {
        this.apErrorHandler.reportUserVisibleError(error);
      }

      return [error.message];
    }

    if (error instanceof HttpErrorResponse && error.error) {
      const httpErrors = this.findErrors(error.error);
      if (httpErrors.length) {
        return httpErrors;
      }

      if (error.statusText) {
        return [
          'An error has occurred. Status: ' + error.status + ': ' + error.statusText,
        ];
      }
    }

    if (isAngularJsHttpResponse(error) && error.data) {
      const httpErrors = this.findErrors(error.data);
      if (httpErrors.length) {
        return httpErrors;
      }

      if (error.status && error.statusText) {
        return [
          'An error has occurred. Status: ' + error.status + ': ' + error.statusText,
        ];
      }
    }

    const errors = this.findErrors(error);
    if (errors.length) {
      return errors;
    }

    return [genericErrorMessage];
  }

  getTranslatedErrors(error: unknown, skipReporting?: boolean): Observable<string[]> {
    return this.translateService.get(this.getErrors(error, skipReporting)).pipe(
      map(translations => {
        return flatMap(translations);
      }),
    );
  }

  private findErrors(error: unknown): string[] {
    if (isModelStateError(error)) {
      const modelStateErrors = flatMap(error.ModelState, value => {
        return value || [];
      });
      if (modelStateErrors.length) {
        return modelStateErrors;
      }
    }

    if (isCommandResult(error) && error.ValidationResult && !error.ValidationResult.IsValid) {
      const validationResultErrors = error.ValidationResult.Errors.map(x => x.ErrorMessage);
      if (validationResultErrors.length) {
        return validationResultErrors;
      }
    }

    if (isMessageError(error) && error.Message) {
      return [error.Message];
    }

    return [];
  }
}


// todo: move this where necessary
interface MessageError {
  readonly Message: string;
}

function isMessageError(error: any): error is MessageError {
  return error && 'Message' in error;
}

interface ModelStateError {
  readonly ModelState: {
    [key: string]: string[];
  };
}

function isModelStateError(error: any): error is ModelStateError {
  return error && 'ModelState' in error;
}

interface AngularJsHttpResponse {
  data: any;
  status: number;
  statusText?: string;
}

function isAngularJsHttpResponse(error: any): error is AngularJsHttpResponse {
  return error && 'data' in error;
}
